import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const MeetingDetailsModal = ({ isOpen, onClose, meeting, onUpdate }) => {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && meeting) {
      fetchAttendees();
    }
  }, [isOpen, meeting]);

  const fetchAttendees = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('meeting_attendees')
        ?.select(`
          user_id,
          user_profiles:user_id(full_name, email, role)
        `)
        ?.eq('meeting_id', meeting?.id);

      if (error) throw error;

      setAttendees(data?.map(a => a?.user_profiles) || []);
    } catch (error) {
      console.error('Error fetching attendees:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (!isOpen || !meeting) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Meeting Details</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h4 className="text-2xl font-bold text-gray-900 mb-2">{meeting?.title}</h4>
            <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
              meeting?.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
              meeting?.status === 'completed'? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {meeting?.status?.toUpperCase()}
            </span>
          </div>

          {meeting?.description && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Description</p>
              <p className="text-gray-600">{meeting?.description}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Date</p>
              <div className="flex items-center gap-2 text-gray-900">
                <Icon name="Calendar" size={16} />
                <span>{formatDate(meeting?.meeting_date)}</span>
              </div>
            </div>

            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Time</p>
              <div className="flex items-center gap-2 text-gray-900">
                <Icon name="Clock" size={16} />
                <span>{formatTime(meeting?.meeting_time)}</span>
                {meeting?.duration_minutes && (
                  <span className="text-sm text-gray-500">({meeting?.duration_minutes} min)</span>
                )}
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Location</p>
            <div className="flex items-center gap-2 text-gray-900 capitalize">
              <Icon name={
                meeting?.location_type === 'online' ? 'Video' :
                meeting?.location_type === 'in-person' ? 'MapPin' : 'Users'
              } size={16} />
              <span>{meeting?.location_type}</span>
            </div>
            {meeting?.location_details && (
              <p className="mt-1 text-sm text-gray-600">{meeting?.location_details}</p>
            )}
          </div>

          {meeting?.google_meet_link && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Join Link</p>
              <a
                href={meeting?.google_meet_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-micro"
              >
                <Icon name="Video" size={16} />
                Join Google Meet
              </a>
            </div>
          )}

          {meeting?.organizer && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Organized By</p>
              <div className="flex items-center gap-2 text-gray-900">
                <Icon name="User" size={16} />
                <span>{meeting?.organizer?.full_name}</span>
                <span className="text-sm text-gray-500">({meeting?.organizer?.email})</span>
              </div>
            </div>
          )}

          {meeting?.team && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Team</p>
              <div className="flex items-center gap-2 text-gray-900">
                <Icon name="Users" size={16} />
                <span>{meeting?.team?.team_name}</span>
              </div>
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Attendees ({attendees?.length})
            </p>
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Icon name="Loader2" size={20} className="animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {attendees?.map((attendee, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">
                        {attendee?.full_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{attendee?.full_name}</p>
                      <p className="text-xs text-gray-500 capitalize">{attendee?.role?.replace('_', ' ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingDetailsModal;